/**
 * Per-doc-type upload tile (cr2a). Shows the category, its already-uploaded
 * docs, and a dropzone. Drives the per-file upload pipeline (initiate → PUT →
 * confirm) with idle → uploading → success/error states + an indeterminate
 * progress bar. Accepts PDF + Excel, guards 25 MB client-side.
 */
import { useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Upload, CheckCircle2, AlertCircle, Loader2, Trash2, ExternalLink } from 'lucide-react';
import type { DocumentSummary, DocType } from '@klinik/shared';
import { Badge } from './ui/Badge';
import {
  docTypeMeta,
  docStatusMeta,
  formatBytes,
  fileKindLabel,
  validateFile,
  ACCEPT_ATTR,
} from '../lib/docs';
import { uploadDocument, type UploadOutcome } from '../lib/useDocuments';

type TileState =
  | { phase: 'idle' }
  | { phase: 'uploading'; filename: string }
  | { phase: 'error'; message: string };

export function UploadTile({
  companyId,
  docType,
  docs,
  onUploaded,
  onOpen,
  onDelete,
  disabled,
}: {
  companyId: string;
  docType: DocType;
  docs: DocumentSummary[];
  onUploaded: (doc: DocumentSummary) => void;
  onOpen: (doc: DocumentSummary) => void;
  onDelete: (doc: DocumentSummary) => void;
  disabled?: boolean;
}) {
  const meta = docTypeMeta(docType);
  const Icon = meta.icon;
  const reduce = useReducedMotion();
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<TileState>({ phase: 'idle' });
  const [dragOver, setDragOver] = useState(false);

  const busy = state.phase === 'uploading' || disabled;
  const totalBytes = docs.reduce((sum, d) => sum + (d.sizeBytes ?? 0), 0);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0 || busy) return;
    // Upload sequentially so the indeterminate bar reflects one active file and
    // a failure stops the batch with a clear message.
    for (const file of Array.from(files)) {
      const invalid = validateFile(file);
      if (invalid) {
        setState({ phase: 'error', message: `${file.name}: ${invalid}` });
        return;
      }
      setState({ phase: 'uploading', filename: file.name });
      const outcome: UploadOutcome = await uploadDocument(companyId, docType, file);
      if (outcome.ok) {
        onUploaded(outcome.document);
      } else {
        setState({ phase: 'error', message: `${file.name}: ${outcome.message}` });
        return;
      }
    }
    setState({ phase: 'idle' });
  }

  function openPicker() {
    if (!busy) inputRef.current?.click();
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5">
      <div className="flex items-center gap-2 text-slate-700">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-blue-50 text-blue-600">
          <Icon size={18} aria-hidden />
        </span>
        <div className="min-w-0 leading-tight">
          <div className="truncate text-sm font-semibold">{meta.label}</div>
          <div className="text-[11px] text-slate-400">
            {docs.length > 0 ? `${docs.length} file · ${formatBytes(totalBytes)}` : 'Belum ada file'}
          </div>
        </div>
      </div>

      {/* Uploaded docs for this category */}
      {docs.length > 0 && (
        <ul className="mt-2.5 space-y-1.5">
          {docs.map((d) => {
            const s = docStatusMeta(d.status);
            return (
              <li
                key={d.id}
                className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs"
              >
                <span className="shrink-0 rounded bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
                  {fileKindLabel(d.filename, d.contentType)}
                </span>
                <button
                  onClick={() => onOpen(d)}
                  className="flex-1 truncate text-left font-medium text-slate-600 hover:text-brand"
                  title={`Buka ${d.filename}`}
                >
                  {d.filename}
                </button>
                <Badge tone={s.tone}>{s.label}</Badge>
                <button
                  onClick={() => onOpen(d)}
                  aria-label={`Buka ${d.filename}`}
                  className="text-slate-300 hover:text-brand"
                >
                  <ExternalLink size={14} aria-hidden />
                </button>
                <button
                  onClick={() => onDelete(d)}
                  aria-label={`Hapus ${d.filename}`}
                  className="text-slate-300 hover:text-red-500"
                >
                  <Trash2 size={14} aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Dropzone / status */}
      <div className="mt-3">
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          className="hidden"
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <AnimatePresence mode="wait">
          {state.phase === 'uploading' ? (
            <motion.div
              key="uploading"
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="rounded-lg border border-blue-100 bg-blue-50/60 p-2.5"
            >
              <div className="flex items-center gap-2 text-xs font-semibold text-blue-700">
                <Loader2 size={14} className="animate-spin" aria-hidden />
                <span className="truncate">Mengunggah {state.filename}…</span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-blue-100">
                <motion.div
                  className="h-full w-1/3 rounded-full bg-brand"
                  animate={reduce ? {} : { x: ['-100%', '300%'] }}
                  transition={{ repeat: Infinity, duration: 1.1, ease: 'easeInOut' }}
                />
              </div>
            </motion.div>
          ) : state.phase === 'error' ? (
            <motion.button
              key="error"
              type="button"
              onClick={openPicker}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex w-full items-start gap-2 rounded-lg border border-red-100 bg-red-50 p-2.5 text-left text-xs text-red-700"
            >
              <AlertCircle size={14} className="mt-0.5 shrink-0" aria-hidden />
              <span className="flex-1">{state.message} · Klik untuk coba lagi.</span>
            </motion.button>
          ) : (
            <button
              key="idle"
              type="button"
              onClick={openPicker}
              onDragOver={(e) => {
                e.preventDefault();
                if (!busy) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                void handleFiles(e.dataTransfer.files);
              }}
              disabled={busy}
              className={`flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed py-2 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                dragOver
                  ? 'border-brand bg-blue-50 text-brand'
                  : docs.length > 0
                    ? 'border-slate-200 text-slate-500 hover:border-brand hover:text-brand'
                    : 'border-slate-300 text-brand hover:bg-blue-50'
              }`}
            >
              {docs.length > 0 ? (
                <CheckCircle2 size={14} aria-hidden />
              ) : (
                <Upload size={14} aria-hidden />
              )}
              {docs.length > 0 ? 'Tambah file' : 'Unggah file (PDF/Excel)'}
            </button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
